import type { CourseData } from '@slideforge/shared';
import { strFromU8, strToU8, unzipSync, zipSync, type Unzipped } from 'fflate';
import { getClip, putClip, type StoredClip } from './blob-store.js';
import { sha256Hex } from './hash.js';

const MANIFEST_PATH = 'slideforge-narration.json';
const SHA_256 = /^[a-f0-9]{64}$/u;
const MAX_ARCHIVE_BYTES = 300 * 1024 * 1024;
const MAX_EXPANDED_BYTES = 500 * 1024 * 1024;
const MAX_ASSETS = 10_000;

interface PortableAssetEntry {
  readonly audioId: string;
  readonly mimeType: string;
  readonly byteLength: number;
}

interface PortableNarrationManifest {
  readonly format: 'slideforge-narration';
  readonly version: 1;
  readonly courseId: string;
  readonly assets: readonly PortableAssetEntry[];
}

export class PortableNarrationError extends Error {
  constructor(
    readonly code: 'invalid-bundle' | 'wrong-course' | 'missing-asset' | 'integrity-failed' | 'too-large',
    message: string,
  ) {
    super(message);
    this.name = 'PortableNarrationError';
  }
}

export interface PortableNarrationExport {
  readonly blob: Blob;
  readonly assetCount: number;
  readonly filename: string;
}

export async function exportPortableNarration(
  course: CourseData,
  resolve: (audioId: string) => Promise<StoredClip | undefined> = getClip,
): Promise<PortableNarrationExport> {
  const ids = narrationAudioIds(course);
  if (ids.length > MAX_ASSETS || course.courseId.length > 200) {
    throw new PortableNarrationError('invalid-bundle', 'The course narration metadata is too large to export safely.');
  }
  const assets: PortableAssetEntry[] = [];
  const entries: Record<string, Uint8Array> = {};
  let totalBytes = 0;

  for (const audioId of ids) {
    if (!SHA_256.test(audioId)) {
      throw new PortableNarrationError('integrity-failed', `Narration audioId ${audioId} is not a SHA-256 digest.`);
    }
    const clip = await resolve(audioId);
    if (!clip) {
      throw new PortableNarrationError(
        'missing-asset',
        `Narration ${audioId} is missing on this machine and cannot be exported.`,
      );
    }
    const bytes = new Uint8Array(await clip.blob.arrayBuffer());
    totalBytes += bytes.byteLength;
    if (totalBytes > MAX_EXPANDED_BYTES) {
      throw new PortableNarrationError('too-large', 'The narration assets exceed the 500 MB export limit.');
    }
    if (
      clip.audioId !== audioId ||
      clip.byteLength !== bytes.byteLength ||
      clip.mimeType.length === 0 ||
      clip.mimeType.length > 200 ||
      clip.mimeType.includes('\r') ||
      clip.mimeType.includes('\n') ||
      await sha256Hex(bytes.buffer) !== audioId
    ) {
      throw new PortableNarrationError('integrity-failed', `Narration ${audioId} failed its SHA-256 integrity check.`);
    }
    assets.push({ audioId, mimeType: clip.mimeType, byteLength: bytes.byteLength });
    entries[`audio/${audioId}`] = bytes;
  }

  const manifest: PortableNarrationManifest = {
    format: 'slideforge-narration',
    version: 1,
    courseId: course.courseId,
    assets,
  };
  entries[MANIFEST_PATH] = strToU8(JSON.stringify(manifest));
  const archive = zipSync(entries, { level: 6 });
  return {
    blob: new Blob([archive.buffer], { type: 'application/zip' }),
    assetCount: assets.length,
    filename: `${safeFilename(course.courseId)}.slideforge-audio.zip`,
  };
}

export async function importPortableNarration(
  archive: Blob,
  expectedCourseId: string,
  store: (blob: Blob) => Promise<StoredClip> = putClip,
): Promise<number> {
  if (archive.size > MAX_ARCHIVE_BYTES) {
    throw new PortableNarrationError('too-large', 'The narration bundle is larger than the 300 MB import limit.');
  }

  let expandedBytes = 0;
  let files: Unzipped;
  try {
    files = unzipSync(new Uint8Array(await archive.arrayBuffer()), {
      filter: (file) => {
        expandedBytes += file.originalSize;
        if (expandedBytes > MAX_EXPANDED_BYTES) {
          throw new PortableNarrationError('too-large', 'The expanded narration bundle exceeds 500 MB.');
        }
        return file.name === MANIFEST_PATH || file.name.startsWith('audio/');
      },
    });
  } catch (error) {
    if (error instanceof PortableNarrationError) throw error;
    throw new PortableNarrationError('invalid-bundle', 'The selected file is not a valid narration bundle.');
  }

  const manifestBytes = files[MANIFEST_PATH];
  if (!manifestBytes) throw new PortableNarrationError('invalid-bundle', 'The narration bundle has no manifest.');
  const manifest = parseManifest(manifestBytes);
  if (manifest.courseId !== expectedCourseId) {
    throw new PortableNarrationError(
      'wrong-course',
      `This narration bundle belongs to course ${manifest.courseId}, not ${expectedCourseId}.`,
    );
  }

  // Validate every file before writing any of them, so a corrupt bundle cannot
  // leave a half-imported course behind.
  const validated: { entry: PortableAssetEntry; bytes: Uint8Array }[] = [];
  for (const entry of manifest.assets) {
    const bytes = files[`audio/${entry.audioId}`];
    if (!bytes || bytes.byteLength !== entry.byteLength) {
      throw new PortableNarrationError('missing-asset', `Narration ${entry.audioId} is missing or truncated.`);
    }
    if (await sha256Hex(bytes.buffer) !== entry.audioId) {
      throw new PortableNarrationError('integrity-failed', `Narration ${entry.audioId} failed its SHA-256 integrity check.`);
    }
    validated.push({ entry, bytes });
  }

  for (const { entry, bytes } of validated) {
    const stored = await store(new Blob([ownedArrayBuffer(bytes)], { type: entry.mimeType }));
    if (stored.audioId !== entry.audioId) {
      throw new PortableNarrationError('integrity-failed', `Narration ${entry.audioId} changed while being stored.`);
    }
  }
  return validated.length;
}

export function narrationAudioIds(course: CourseData): readonly string[] {
  const ids = new Set<string>();
  for (const namespaces of Object.values(course.slides)) {
    for (const take of namespaces.narration?.takes ?? []) ids.add(take.audioId);
  }
  return [...ids].sort();
}

function parseManifest(bytes: Uint8Array): PortableNarrationManifest {
  let value: unknown;
  try {
    value = JSON.parse(strFromU8(bytes));
  } catch {
    throw new PortableNarrationError('invalid-bundle', 'The narration bundle manifest is invalid JSON.');
  }
  if (!isRecord(value) || value['format'] !== 'slideforge-narration' || value['version'] !== 1) {
    throw new PortableNarrationError('invalid-bundle', 'The narration bundle format is not supported.');
  }
  if (
    typeof value['courseId'] !== 'string' ||
    value['courseId'].length === 0 ||
    value['courseId'].length > 200
  ) {
    throw new PortableNarrationError('invalid-bundle', 'The narration bundle has no course id.');
  }
  if (!Array.isArray(value['assets']) || value['assets'].length > MAX_ASSETS) {
    throw new PortableNarrationError('invalid-bundle', 'The narration bundle asset list is invalid.');
  }

  const seen = new Set<string>();
  const assets = value['assets'].map((candidate) => {
    if (!isRecord(candidate)) throw new PortableNarrationError('invalid-bundle', 'A narration asset entry is invalid.');
    const audioId = candidate['audioId'];
    const mimeType = candidate['mimeType'];
    const byteLength = candidate['byteLength'];
    if (
      typeof audioId !== 'string' ||
      !SHA_256.test(audioId) ||
      seen.has(audioId) ||
      typeof mimeType !== 'string' ||
      mimeType.length === 0 ||
      mimeType.length > 200 ||
      mimeType.includes('\r') ||
      mimeType.includes('\n') ||
      typeof byteLength !== 'number' ||
      !Number.isSafeInteger(byteLength) ||
      byteLength < 0
    ) {
      throw new PortableNarrationError('invalid-bundle', 'A narration asset entry is invalid.');
    }
    seen.add(audioId);
    return { audioId, mimeType, byteLength };
  });
  return { format: 'slideforge-narration', version: 1, courseId: value['courseId'], assets };
}

function safeFilename(value: string): string {
  return value.replace(/[^a-zA-Z0-9._-]+/gu, '-').replace(/^-+|-+$/gu, '') || 'course';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function ownedArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}
